import React from 'react';
import { Container } from 'react-bootstrap';
import { Control, FieldErrors, UseFormRegister, useFieldArray, useForm } from 'react-hook-form';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import SpinnerButton from '../Common/SpinnerButton';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import BoldButton from '../Common/BoldButton';
import useGetFieldAlyticsSites, { FieldAlyticsSite } from '../../requests/fieldalytics/useGetFieldAlyticsSites';
import useUpdateFieldAlyticsSites from '../../requests/fieldalytics/useUpdateFieldAlyticsSites';
import { useUserOrKaEmployee } from '../UserAccess/useUserOrKaEmployee';
import { LoginType } from '../UserAccess/UserContext';

interface FormSite {
  id: string;
  name: string;
  blenders: { id: string }[];
}

interface SiteMappingForm {
  sites: FormSite[];
}

interface SiteRowProps {
  pIndex: number;
  control: Control<SiteMappingForm>;
  register: UseFormRegister<SiteMappingForm>;
  errors: FieldErrors<SiteMappingForm>;
}

const SiteRow = ({ pIndex, control, register, errors }: SiteRowProps) => {
  const { fields, remove, append } = useFieldArray({
    control,
    name: `sites.${pIndex}.blenders`
  });

  return <>
    {fields.map((field, index) => {
      const err = errors.sites?.[pIndex]?.blenders?.[index]?.id;
      return <div key={field.id} className="d-flex">
        <div className="w-100 py-1">
          <input
            type="text"
            key={field.id}
            data-testid={`sites-${pIndex}-${index}-id`}
            {...register(`sites.${pIndex}.blenders.${index}.id`, {
              required: (index > 0 && fields.length > 1) && 'Enter an ID or remove this entry'
            })}
            className={`border-dark form-control${err ? ' is-invalid' : ''}`} />
        </div>
        {fields.length > 1 &&
          <BoldButton
            className='btn-close btn-sm mt-2 ms-2'
            data-testid={`sites-${pIndex}-${index}-remove`}
            onClick={() => remove(index)} />}
        <span className="invalid-feedback">{err?.message}</span>
      </div>;
    })}
    <div className="text-end pt-1">
      <BoldButton
        className='col-auto btn-ghost-primary'
        data-testid={`sites-${pIndex}-add`}
        onClick={() => append({ id: '' })}>
        Add Mapping
      </BoldButton>
    </div>
  </>;
};

const ToFormSite = (site: FieldAlyticsSite): FormSite => {
  const blenderIds = site.blenderIds.length === 0 ? [''] : [...site.blenderIds];
  return {
    ...site,
    blenders: blenderIds.map(id => ({ id }))
  };
};

interface SiteMappingTableProps {
  sites: FieldAlyticsSite[];
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
  disableSave: boolean;
}

const SiteMappingTable = ({
  sites,
  displayMessageSuccess,
  displayMessageFail,
  disableSave,
}: SiteMappingTableProps) => {
  const { register, handleSubmit, control, formState: { errors } } = useForm<SiteMappingForm>({
    defaultValues: { sites: sites.map(ToFormSite) }
  });

  const updateSites = useUpdateFieldAlyticsSites({
    onSuccess: () => displayMessageSuccess('Saved successfully'),
    onError: () => displayMessageFail('Unknown error, please contact Kahler Automation'),
  });

  const submit = (form: SiteMappingForm) => {
    form.sites.forEach(s => {
      if (s.blenders.length === 1 && !s.blenders[0].id) {
        s.blenders.pop();
      }
    });

    updateSites.request(form.sites.map(s => ({
      id: s.id,
      blenderIds: s.blenders.map(b => b.id),
    })));
  };

  return <>
    <table className="table table-hover table-bordered">
      <thead>
        <tr>
          <th className="w-50" scope="col">Constellation Site Name</th>
          <th className="w-50" scope="col">FieldAlytics Blender IDs</th>
        </tr>
      </thead>
      <tbody>
        {sites.map((s, index) =>
          <tr key={s.id}>
            <th scope="row">
              <label>{s.name}</label>
            </th>
            <td>
              <SiteRow pIndex={index} register={register} control={control} errors={errors} />
            </td>
          </tr>)}
      </tbody>
    </table>
    <div className="w-100 mt-4">
      <SpinnerButton
        className="float-end w-25 btn-ghost-primary"
        onClick={handleSubmit(submit)}
        spinning={updateSites.isLoading}
        disabled={disableSave}>
        Save
      </SpinnerButton>
    </div>
  </>;
};

interface SiteMappingViewOnlyTableProps {
  sites: FieldAlyticsSite[];
}

const SiteMappingViewOnlyTable = ({ sites }: SiteMappingViewOnlyTableProps) =>
  <table className="table table-hover table-bordered">
    <thead>
      <tr>
        <th className="w-50">Constellation Site Name</th>
        <th className="w-50">FieldAlytics Blender IDs</th>
      </tr>
    </thead>
    <tbody>
      {sites.map(s => (
        <tr key={s.id}>
          <td>{s.name}</td>
          <td>{s.blenderIds.join(', ')}</td>
        </tr>
      ))}
    </tbody>
  </table>;

export const FieldAlyticsSiteMappingPage = () => {
  const { userType } = useUserOrKaEmployee();
  const canEdit = userType === LoginType.User;
  const displayMessage = useDisplayMessage();
  const getSites = useGetFieldAlyticsSites({
    onError: (e) => displayMessage.fail(e.message),
  });

  return <Container>
    <h2 className="mb-5 ka-blue">FieldAlytics - Site Mapping</h2>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    {getSites.isLoading
      ? <CenteredSpinner />
      : canEdit
        ? <SiteMappingTable
          key={getSites.data ? 'loaded' : 'empty'}
          sites={getSites.data ?? []}
          displayMessageSuccess={displayMessage.success}
          displayMessageFail={displayMessage.fail}
          disableSave={!getSites.data}
        />
        : <SiteMappingViewOnlyTable sites={getSites.data ?? []} />}
  </Container>;
};
